import {
  type AgentDraft,
  type AgentDraftGoal,
  type TemplateVariable,
  TemplateVariableType,
} from "@/shared/domain/agents";

/**
 * Per-variable metadata the user can edit in the editor. Keyed by
 * variable name so an entry can survive a placeholder being temporarily
 * deleted and re-typed without losing the user's typed description /
 * default. Stale entries (whose name no longer matches any detected
 * placeholder) are dropped at save time by `buildVariables`.
 */
export interface VariableMeta {
  description: string;
  default: string;
}

/** Fixed description for the implicit `{{dataset.id}}` variable — the row
 *  is always present whenever a dataset goal is declared, and we don't
 *  let the user edit it because the meaning is invariant. */
export const IMPLICIT_DATASET_DESCRIPTION =
  "The dataset the agent will operate on. Selected at launch time.";

export interface DatasetSummaryGoalState {
  goal_type: "dataset_summary";
  /** Raw textarea contents; empty string means "omit on save and use the
   *  handler's opinionated default." Validated 1-4000 chars when non-empty
   *  to mirror :class:`DatasetSummaryAgentGoal.summary_format_spec_prompt`. */
  summary_format_spec_prompt: string;
}

export interface DatasetTriageGoalState {
  goal_type: "dataset_triage";
  /** Editable list rather than a dict so the user can reorder rows and
   *  the UI can render stable keys; converted to a ``Record`` on save. */
  label_vocabulary: Array<{ label: string; description: string }>;
}

export interface CreateEventsGoalState {
  goal_type: "create_events";
  /** Fixed event types the agent may create — each row's ``name`` becomes a
   *  created event's name. Stored as an array (not a Record) so rows reorder
   *  and React keys stay stable; flattened to a Record on save. Mirrors
   *  :class:`CreateEventsGoal.event_vocabulary`. */
  event_vocabulary: Array<{ name: string; description: string }>;
  /** Fixed tags the agent may attach to created events, each with a
   *  description of when it applies. May be empty. Mirrors
   *  :class:`CreateEventsGoal.tag_vocabulary`. */
  tag_vocabulary: Array<{ tag: string; description: string }>;
  /** When true, created events are filed into a collection chosen at launch
   *  time (via the implicit ``{{collection.id}}`` variable). When false the
   *  goal carries no ``collection_id`` and events are not collected. */
  collection_enabled: boolean;
  /** Raw textarea contents; empty string means "omit on save". Layered on
   *  top of the vocabularies. Mirrors :class:`CreateEventsGoal.event_focus_prompt`. */
  event_focus_prompt: string;
}

export type GoalState =
  DatasetSummaryGoalState | DatasetTriageGoalState | CreateEventsGoalState;

/**
 * One org-shared skill attached to the agent. Mirrors the server's
 * ``AvailableSkillSpec``: a skill reference plus an optional pinned version.
 *
 * The skill must be one of the org-shared tiers (``org`` or ``org-editable``)
 * — both have org-wide visibility, so they resolve for every teammate who
 * launches the agent. A private skill would not.
 *
 * ``version: null`` means "track latest" — it is omitted from the wire
 * payload so the server resolves ``MAX(version)`` afresh at each invoke. A
 * numeric ``version`` pins that exact version.
 */
export interface SkillSelection {
  skill_id: string;
  version: number | null;
}

/**
 * Source of truth for the editor's form state.
 *
 * The editor stores everything the user can edit as plain text and
 * structured arrays here, then derives the wire-format
 * ``StartAgentSessionRequest`` and the declared-variables list at save
 * time. Keeping every text field in the form state (rather than an
 * already-built JSON dict) is what makes derived-variable detection
 * possible without re-parsing the body.
 */
export interface FormState {
  name: string;
  description: string;
  modelProfile: string | null;
  kickoffMessage: string;
  goals: GoalState[];
  /** Org-shared skills the agent's AI may invoke. Order is preserved on the
   *  wire; an empty list is meaningful (see `buildRequestTemplate`). */
  skills: SkillSelection[];
  /** User-editable description / default for each variable, keyed by name.
   *  The implicit `dataset.id` row's description is ignored here (see
   *  IMPLICIT_DATASET_DESCRIPTION). Stale keys (variables no longer
   *  referenced by any placeholder) are dropped on save. */
  variableMeta: Record<string, VariableMeta>;
  /**
   * Saved goals of a type the editor cannot edit, such as a session goal
   * created through the SDK. Written back on save exactly as they were read,
   * at their saved positions, so saving an agent never deletes or reorders
   * one.
   */
  passthroughGoals: PassthroughGoal[];
  /**
   * Saved variable declarations the editor cannot produce: those a
   * passthrough goal names, and those whose type differs from the one the
   * editor gives that name (see {@link editorVariableType}), such as a
   * custom-named dataset variable. Shown read-only in the Variables panel and
   * written back verbatim while still named.
   */
  passthroughVariables: TemplateVariable[];
}

/** A saved goal the editor cannot edit, and where it sat in the saved list. */
export interface PassthroughGoal {
  /** Index in the saved ``goals`` list, so a save puts the goal back there. */
  savedIndex: number;
  goal: Record<string, unknown>;
}

/** The implicit, undeletable variable name injected into every dataset-typed
 *  goal at save time. Mirrored as a synthetic row in the variables panel. */
export const IMPLICIT_DATASET_VAR_NAME = "dataset.id";

/** Placeholder that replaces a dataset goal's ``dataset_id`` field. */
export const IMPLICIT_DATASET_PLACEHOLDER = `{{${IMPLICIT_DATASET_VAR_NAME}}}`;

/** Fixed description for the implicit `{{collection.id}}` variable — present
 *  whenever a create-events goal is declared, and not user-editable because
 *  the meaning is invariant. */
export const IMPLICIT_COLLECTION_DESCRIPTION =
  "The collection created events are added to. Selected at launch time.";

/** The implicit, undeletable variable name injected into every create-events
 *  goal at save time. Mirrored as a synthetic row in the variables panel. */
export const IMPLICIT_COLLECTION_VAR_NAME = "collection.id";

/** Placeholder that replaces a create-events goal's ``collection_id`` field. */
export const IMPLICIT_COLLECTION_PLACEHOLDER = `{{${IMPLICIT_COLLECTION_VAR_NAME}}}`;

/** Mirror of the server-side ``_PLACEHOLDER_RE`` so the form and the
 *  resolver agree on which substrings count as a variable reference. */
const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g;

/**
 * Returns true when at least one declared goal carries an implicit
 * dataset slot. Currently every goal in the discriminated union is
 * dataset-typed, but the predicate is kept explicit so a future
 * non-dataset goal won't silently inject a ``{{dataset.id}}`` row.
 */
export function anyGoalUsesImplicitDataset(goals: GoalState[]): boolean {
  return goals.some(
    (g) =>
      g.goal_type === "dataset_summary" ||
      g.goal_type === "dataset_triage" ||
      g.goal_type === "create_events",
  );
}

/**
 * Returns true when at least one declared goal carries an implicit
 * collection slot. Only the create-events goal does today.
 */
export function anyGoalUsesImplicitCollection(goals: GoalState[]): boolean {
  return goals.some(
    (g) => g.goal_type === "create_events" && g.collection_enabled,
  );
}

function pushPlaceholders(text: string, found: Set<string>): void {
  for (const match of text.matchAll(PLACEHOLDER_RE)) {
    found.add(match[1]);
  }
}

/** Every placeholder in the string values of a JSON value, keys excluded, as
 *  the server walks a request template. */
function pushValuePlaceholders(value: unknown, found: Set<string>): void {
  if (typeof value === "string") {
    pushPlaceholders(value, found);
  } else if (Array.isArray(value)) {
    for (const item of value) {
      pushValuePlaceholders(item, found);
    }
  } else if (typeof value === "object" && value !== null) {
    for (const item of Object.values(value)) {
      pushValuePlaceholders(item, found);
    }
  }
}

/**
 * The type {@link buildVariables} declares a variable of this name with: the
 * two implicit goal variables carry their id types, and every name a person
 * types is a string.
 */
function editorVariableType(name: string): TemplateVariableType {
  if (name === IMPLICIT_DATASET_VAR_NAME) {
    return TemplateVariableType.DATASET_ID;
  }
  if (name === IMPLICIT_COLLECTION_VAR_NAME) {
    return TemplateVariableType.COLLECTION_ID;
  }
  return TemplateVariableType.STRING;
}

/**
 * Walks every text-bearing field in the form state and returns the set of
 * ``{{name}}`` placeholders. Mirrors the server's body walk but operates
 * on the structured form rather than the serialized JSON, so the variables
 * panel can update on every keystroke without re-running
 * ``buildRequestTemplate``. Passthrough goals are walked as saved, so the
 * panel lists the variables they name and a save declares them.
 *
 * Identity fields (``name``, ``description``) are NOT walked because
 * they're agent metadata, not part of the request body that the resolver
 * sees. ``modelProfile`` is also skipped — it's a profile id selector,
 * not freeform text. Label-vocabulary *keys* are also skipped to match
 * the server's "values only" walk, which exists so a placeholder in a
 * key can't silently collide with another key after substitution.
 */
export function collectPlaceholders(formState: FormState): Set<string> {
  const found = new Set<string>();
  pushPlaceholders(formState.kickoffMessage, found);
  for (const { goal } of formState.passthroughGoals) {
    pushValuePlaceholders(goal, found);
  }
  for (const goal of formState.goals) {
    if (goal.goal_type === "dataset_summary") {
      pushPlaceholders(goal.summary_format_spec_prompt, found);
    } else if (goal.goal_type === "dataset_triage") {
      for (const row of goal.label_vocabulary) {
        pushPlaceholders(row.description, found);
      }
    } else {
      pushPlaceholders(goal.event_focus_prompt, found);
      for (const row of goal.event_vocabulary) {
        pushPlaceholders(row.description, found);
      }
      for (const row of goal.tag_vocabulary) {
        pushPlaceholders(row.description, found);
      }
    }
  }
  if (anyGoalUsesImplicitDataset(formState.goals)) {
    found.add(IMPLICIT_DATASET_VAR_NAME);
  }
  if (anyGoalUsesImplicitCollection(formState.goals)) {
    found.add(IMPLICIT_COLLECTION_VAR_NAME);
  }
  return found;
}

/**
 * Converts the placeholder set into a list of ``TemplateVariable``
 * declarations ready for the create / update wire payload. The
 * implicit dataset row gets :data:`TemplateVariableType.DATASET_ID` and
 * a fixed description; user-introduced variables get
 * :data:`TemplateVariableType.STRING` and pick up the editor-supplied
 * description / default from `variableMeta` (empty strings collapse to
 * `null` so they're omitted on the wire). A name in `passthroughVariables`
 * is written as it was saved instead.
 *
 * The output is sorted so commits and snapshots are stable.
 */
export function buildVariables(formState: FormState): TemplateVariable[] {
  const keptByName = new Map(
    formState.passthroughVariables.map((variable) => [variable.name, variable]),
  );
  const placeholders = collectPlaceholders(formState);
  return [...placeholders].sort().map((name): TemplateVariable => {
    const kept = keptByName.get(name);
    if (kept !== undefined) {
      return kept;
    }
    if (name === IMPLICIT_DATASET_VAR_NAME) {
      return {
        name,
        type: TemplateVariableType.DATASET_ID,
        description: IMPLICIT_DATASET_DESCRIPTION,
        required: true,
        default: null,
      };
    }
    if (name === IMPLICIT_COLLECTION_VAR_NAME) {
      // The author may pre-fill a default collection so the launch page
      // doesn't force a picker click every time. The description is still
      // fixed — see IMPLICIT_COLLECTION_DESCRIPTION — because it explains
      // the variable's meaning, not its current value.
      const meta = formState.variableMeta[name];
      const defaultValue = meta?.default ?? "";
      return {
        name,
        type: TemplateVariableType.COLLECTION_ID,
        // The create-events goal only ever files into event collections, so
        // the launch-page picker is hard-filtered to that resource type.
        collection_content_type: "event",
        description: IMPLICIT_COLLECTION_DESCRIPTION,
        required: true,
        default: defaultValue === "" ? null : defaultValue,
      };
    }
    const meta = formState.variableMeta[name];
    const description = meta?.description.trim() ?? "";
    const defaultValue = meta?.default ?? "";
    return {
      name,
      type: TemplateVariableType.STRING,
      description: description === "" ? null : description,
      required: true,
      default: defaultValue === "" ? null : defaultValue,
    };
  });
}

type RequestTemplateJson = Record<string, unknown>;

function buildGoalJson(goal: GoalState): Record<string, unknown> {
  if (goal.goal_type === "dataset_summary") {
    const summary: Record<string, unknown> = {
      goal_type: "dataset_summary",
      dataset_id: IMPLICIT_DATASET_PLACEHOLDER,
    };
    const trimmed = goal.summary_format_spec_prompt.trim();
    if (trimmed.length > 0) {
      summary.summary_format_spec_prompt = goal.summary_format_spec_prompt;
    }
    return summary;
  }
  if (goal.goal_type === "create_events") {
    const eventVocabulary: Record<string, string> = {};
    for (const row of goal.event_vocabulary) {
      if (row.name !== "") {
        eventVocabulary[row.name] = row.description;
      }
    }
    const createEvents: Record<string, unknown> = {
      goal_type: "create_events",
      dataset_id: IMPLICIT_DATASET_PLACEHOLDER,
      event_vocabulary: eventVocabulary,
    };
    const tagVocabulary: Record<string, string> = {};
    for (const row of goal.tag_vocabulary) {
      if (row.tag !== "") {
        tagVocabulary[row.tag] = row.description;
      }
    }
    // Omit an empty tag_vocabulary — the server defaults it to {} — so a
    // parse → build → parse round-trip on a goal with no tags is stable.
    if (Object.keys(tagVocabulary).length > 0) {
      createEvents.tag_vocabulary = tagVocabulary;
    }
    if (goal.collection_enabled) {
      createEvents.collection_id = IMPLICIT_COLLECTION_PLACEHOLDER;
    }
    const trimmed = goal.event_focus_prompt.trim();
    if (trimmed.length > 0) {
      createEvents.event_focus_prompt = goal.event_focus_prompt;
    }
    return createEvents;
  }
  const vocabulary: Record<string, string> = {};
  for (const row of goal.label_vocabulary) {
    if (row.label !== "") {
      vocabulary[row.label] = row.description;
    }
  }
  return {
    goal_type: "dataset_triage",
    dataset_id: IMPLICIT_DATASET_PLACEHOLDER,
    label_vocabulary: vocabulary,
  };
}

/**
 * Builds the request-body half of the wire payload: a JSON dict shaped
 * like :class:`StartAgentSessionRequest`. Omits every field the server
 * defaults sensibly (``system_prompt``, ``client_tools``, ``client_context``,
 * ``analysis_scope``, ``visibility``) so the editor never has to reason
 * about them.
 *
 * ``available_skills`` is the deliberate exception — it is written
 * unconditionally, including as an empty ``[]``. An agent is a shared,
 * reproducible workflow, so its AI-invokable skill set must be explicit:
 * ``[]`` means "no skills" deterministically for every invoker. Omitting
 * the field (the server's ``null`` default) would instead fall back to the
 * invoking user's personal skill subscriptions — non-deterministic and
 * surprising for a shared agent.
 *
 * Returned value is a plain object suitable for ``JSON.stringify``;
 * callers should not mutate it.
 */
export function buildRequestTemplate(
  formState: FormState,
): RequestTemplateJson {
  const body: RequestTemplateJson = {
    goals: interleavePassthroughGoals(
      formState.goals.map(buildGoalJson),
      formState.passthroughGoals,
    ),
    available_skills: formState.skills.map((skill) =>
      skill.version === null
        ? { skill_id: skill.skill_id }
        : { skill_id: skill.skill_id, version: skill.version },
    ),
    // Server-side `AgentTextContent` has only a `text` field — no
    // discriminator — so we send the bare shape rather than a fake
    // `type: "text"` tag the server ignores and that we'd then fail to
    // round-trip in `parseKickoffMessage`.
    messages:
      formState.kickoffMessage.length > 0
        ? [
            {
              role: "user",
              content: [{ text: formState.kickoffMessage }],
            },
          ]
        : [],
  };
  if (formState.modelProfile) {
    body.model_profile = formState.modelProfile;
  }
  return body;
}

/**
 * The editable goals with each passthrough goal put back at its saved index,
 * so a save keeps the saved order (it decides each goal's ``goal_index`` and
 * the order a thread shows them in). An index past the end, left by removing
 * editable goals, appends the goal instead.
 */
function interleavePassthroughGoals(
  editable: Record<string, unknown>[],
  passthrough: readonly PassthroughGoal[],
): Record<string, unknown>[] {
  const goals = [...editable];
  const bySavedIndex = [...passthrough].sort(
    (a, b) => a.savedIndex - b.savedIndex,
  );
  for (const { savedIndex, goal } of bySavedIndex) {
    goals.splice(savedIndex, 0, goal);
  }
  return goals;
}

interface SavedAgent {
  name: string;
  description?: string | null;
  request_template: Record<string, unknown>;
  variables: TemplateVariable[];
}

/**
 * Best-effort reverse of :func:`buildRequestTemplate` for edit mode.
 *
 * Fields outside the editor's shape (legacy fields, future fields we don't
 * handle) are dropped. Goals and variables the editor cannot produce are the
 * exception: a goal of a type it cannot edit, such as a session goal created
 * through the SDK, is kept in ``passthroughGoals``, and the declarations in
 * ``passthroughVariables``, so a save writes them back unchanged instead of
 * deleting or retyping them.
 */
export function parseSavedAgent(saved: SavedAgent): FormState {
  const body = saved.request_template;
  const { goals, passthroughGoals } = parseGoals(body);
  return {
    name: saved.name,
    description: saved.description ?? "",
    modelProfile: parseModelProfile(body),
    kickoffMessage: parseKickoffMessage(body),
    goals,
    skills: parseSkills(body),
    variableMeta: parseVariableMeta(saved.variables),
    passthroughGoals,
    passthroughVariables: parsePassthroughVariables(
      saved.variables,
      passthroughGoals,
    ),
  };
}

/**
 * The saved variables {@link buildVariables} would not write back as they
 * are: those a passthrough goal names, whatever their type, and those whose
 * type is not the one {@link editorVariableType} gives their name.
 */
function parsePassthroughVariables(
  variables: readonly TemplateVariable[],
  passthroughGoals: readonly PassthroughGoal[],
): TemplateVariable[] {
  const named = new Set<string>();
  for (const { goal } of passthroughGoals) {
    pushValuePlaceholders(goal, named);
  }
  return variables.filter(
    (variable) =>
      named.has(variable.name) ||
      variable.type !== editorVariableType(variable.name),
  );
}

/**
 * Reverse of the ``available_skills`` half of {@link buildRequestTemplate}.
 *
 * Reads ``request_template.available_skills`` — a list of
 * ``{ skill_id, version? }`` — into {@link SkillSelection} rows. A missing or
 * non-array value (a legacy agent saved before the Skills section existed, or
 * the server's ``null`` default) yields an empty list; re-saving that agent
 * through the editor then writes an explicit ``[]``. Entries without a
 * string ``skill_id`` are dropped, and a non-numeric ``version`` collapses to
 * ``null`` (track latest).
 */
function parseSkills(body: Record<string, unknown>): SkillSelection[] {
  const available = body.available_skills;
  if (!Array.isArray(available)) {
    return [];
  }
  const parsed: SkillSelection[] = [];
  for (const raw of available) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const obj = raw as Record<string, unknown>;
    if (typeof obj.skill_id !== "string") {
      continue;
    }
    parsed.push({
      skill_id: obj.skill_id,
      version: typeof obj.version === "number" ? obj.version : null,
    });
  }
  return parsed;
}

/**
 * Build a {@link VariableMeta} entry from raw description + default
 * strings, applying the trim-then-collapse rule both code paths into
 * ``variableMeta`` share: trim the description, treat empty / missing
 * defaults as no default declared, and return ``null`` when the entry
 * carries no user-supplied content so the surrounding loops can skip it.
 *
 * Centralising this rule keeps every path into ``variableMeta`` producing the
 * same shape for the same logical agent: edit-mode ({@link parseSavedAgent}),
 * create-from-draft ({@link draftToFormState}), and the AI update handoff —
 * all of which reach it through {@link parseVariableMeta}. Without it, e.g. a
 * saved agent with a whitespace-only description survived edit-mode parse
 * while the equivalent draft was dropped — different shapes for the same
 * content.
 */
function normalizeVariableMeta(
  description: string | null | undefined,
  defaultValue: string | null | undefined,
): VariableMeta | null {
  const trimmedDescription = (description ?? "").trim();
  const value = defaultValue ?? "";
  if (trimmedDescription === "" && value === "") {
    return null;
  }
  return { description: trimmedDescription, default: value };
}

/**
 * Builds the editor's ``variableMeta`` map from declared variables.
 *
 * The parameter is the structural subset shared by a saved agent's
 * {@link TemplateVariable} and a drafted `AgentDraftVariable` (not imported
 * here — the structural type is the contract, not either nominal one), so the
 * edit-mode parse and both draft handoffs produce identical shapes for the
 * same variables — the divergence {@link normalizeVariableMeta} exists to
 * prevent.
 */
function parseVariableMeta(
  variables: readonly {
    name: string;
    description?: string | null;
    default?: string | null;
  }[],
): Record<string, VariableMeta> {
  const meta: Record<string, VariableMeta> = {};
  for (const variable of variables) {
    // The implicit dataset row has no user-editable metadata (no default
    // either — the dataset is always chosen at launch time), so skip it
    // outright.
    if (variable.name === IMPLICIT_DATASET_VAR_NAME) {
      continue;
    }
    if (variable.name === IMPLICIT_COLLECTION_VAR_NAME) {
      // The collection's description is fixed (IMPLICIT_COLLECTION_DESCRIPTION),
      // but the author may pre-fill a default collection that the launch
      // page surfaces. Preserve the default; ignore any persisted description.
      const defaultValue = variable.default ?? "";
      if (defaultValue !== "") {
        meta[variable.name] = { description: "", default: defaultValue };
      }
      continue;
    }
    const entry = normalizeVariableMeta(variable.description, variable.default);
    if (entry !== null) {
      meta[variable.name] = entry;
    }
  }
  return meta;
}

function parseModelProfile(body: Record<string, unknown>): string | null {
  const value = body.model_profile;
  return typeof value === "string" && value.length > 0 ? value : null;
}

function parseKickoffMessage(body: Record<string, unknown>): string {
  const messages = body.messages;
  if (!Array.isArray(messages) || messages.length === 0) {
    return "";
  }
  const first = messages[0] as Record<string, unknown> | undefined;
  if (first === undefined || !Array.isArray(first.content)) {
    return "";
  }
  // Server-side `AgentTextContent` is identified by the absence of a
  // `content_type` discriminator on its sibling content types. Pick the
  // first block that has a string `text` field and no other content type
  // tag — this is the canonical text block shape the SDK emits.
  for (const block of first.content) {
    if (typeof block !== "object" || block === null) {
      continue;
    }
    const record = block as Record<string, unknown>;
    if (record.content_type === undefined && typeof record.text === "string") {
      return record.text;
    }
  }
  return "";
}

/**
 * A blank editor form — the starting point for a create, and the base the
 * create handoff overlays a draft onto.
 *
 * A factory rather than a shared constant so no two forms alias the same
 * ``goals`` / ``skills`` arrays.
 */
export function emptyFormState(): FormState {
  return {
    name: "",
    description: "",
    modelProfile: null,
    kickoffMessage: "",
    goals: [],
    skills: [],
    variableMeta: {},
    passthroughGoals: [],
    passthroughVariables: [],
  };
}

/** What the editor last seeded its form from. */
export interface SeededFrom {
  agentId: string;
  draft: AgentDraft | null;
}

/**
 * Whether the edit form should be (re)seeded from ``agentId``'s saved values
 * overlaid with ``draft``, given what it was ``seeded`` from last.
 *
 * Seeding is one-shot per (agent, proposal) pair rather than per agent. The
 * per-agent half is what keeps a refetch — a post-save cache invalidation, a
 * tab-focus refresh — from throwing away edits the user is in the middle of
 * typing. The per-proposal half is what lets a *second* AI proposal land: when
 * the AI updates the agent the user already has open, neither the route nor
 * the fetched agent changes, so an agent-only gate reads the overlay as
 * already applied and the form never moves.
 *
 * Identity, not value, is the test on both: a claimed draft keeps one
 * reference for as long as the claim is held (see ``useDraftSeededForm``), so
 * a re-render carrying the same proposal is a no-op while a freshly claimed
 * one always re-seeds.
 */
export function shouldSeedForm(
  seeded: SeededFrom | null,
  agentId: string,
  draft: AgentDraft | null,
): boolean {
  return (
    seeded === null || seeded.agentId !== agentId || seeded.draft !== draft
  );
}

/**
 * Overlays an {@link AgentDraft} onto an existing {@link FormState}.
 *
 * ``base`` is the form to merge into — a saved agent's form (via
 * {@link parseSavedAgent}) for the update handoff, or
 * {@link emptyFormState} for a create. A field the draft PROVIDES
 * full-replaces the base value; a field it OMITS keeps the base value.
 * ``goals`` and ``variables`` replace the editable goals and the variable
 * metadata when provided. ``passthroughGoals`` and ``passthroughVariables``
 * always survive from ``base``: a draft can propose only the goal kinds the
 * editor edits, so it has no way to restate or remove a goal it cannot see.
 *
 * This is also where the draft's wire representation is translated, so the
 * editor never peeks at it (notably ``label_vocabulary`` is a ``Record`` in
 * the draft and an ordered array in the form). Variables naming the
 * implicit dataset placeholder are dropped by {@link parseVariableMeta},
 * because {@link buildVariables} injects that row automatically and an
 * LLM-supplied entry would duplicate it or fight the fixed
 * {@link IMPLICIT_DATASET_DESCRIPTION}.
 *
 * ``skills`` carry resolved ``skill_id``s — the tool resolves the names the
 * model supplies before stashing, so an unknown or private skill fails as a
 * tool error rather than arriving here unresolvable. Omitting the field keeps
 * the saved agent's skills; an explicit ``[]`` detaches them all. The human
 * reviews and saves the merged form — this only stages the overlay.
 */
export function overlayDraftOnForm(
  base: FormState,
  draft: AgentDraft,
): FormState {
  return {
    ...base,
    name: draft.name ?? base.name,
    description: draft.description ?? base.description,
    modelProfile: draft.model_profile ?? base.modelProfile,
    kickoffMessage: draft.kickoff_message ?? base.kickoffMessage,
    // Both list fields test `undefined` explicitly, so "provided" means the
    // same thing for each. An explicitly-empty `goals: []` / `variables: []`
    // is provided, and clears the base's editable goals / variable metadata.
    goals:
      draft.goals === undefined
        ? base.goals
        : draft.goals.map(draftGoalToFormGoal),
    variableMeta:
      draft.variables === undefined
        ? base.variableMeta
        : parseVariableMeta(draft.variables),
    skills:
      draft.skills === undefined
        ? base.skills
        : draft.skills.map((skill) => ({
            skill_id: skill.skill_id,
            version: skill.version ?? null,
          })),
  };
}

/**
 * Maps an {@link AgentDraft} stashed by the ``client_create_agent`` client
 * tool into a fresh {@link FormState} for the create handoff.
 */
export function draftToFormState(draft: AgentDraft): FormState {
  return overlayDraftOnForm(emptyFormState(), draft);
}

function draftGoalToFormGoal(goal: AgentDraftGoal): GoalState {
  if (goal.goal_type === "dataset_summary") {
    return {
      goal_type: "dataset_summary",
      summary_format_spec_prompt: goal.summary_format_spec_prompt ?? "",
    };
  }
  if (goal.goal_type === "create_events") {
    // Both vocabularies are Records on the wire (so the LLM can emit them
    // as plain JSON objects); the editor stores them as ordered arrays
    // for stable React keys and reordering. `consumeAgentDraft` already
    // drops a create-events goal whose `event_vocabulary` isn't a plain
    // object, so the `?? {}` is defense-in-depth for the same reason as
    // the triage branch below.
    return {
      goal_type: "create_events",
      event_vocabulary: Object.entries(goal.event_vocabulary ?? {}).map(
        ([name, description]) => ({ name, description }),
      ),
      tag_vocabulary: Object.entries(goal.tag_vocabulary ?? {}).map(
        ([tag, description]) => ({ tag, description }),
      ),
      collection_enabled: goal.collection_enabled === true,
      event_focus_prompt: goal.event_focus_prompt ?? "",
    };
  }
  // dataset_triage: the draft's vocabulary is a Record so the LLM can
  // emit it as a plain JSON object. The editor stores rows as an array
  // to preserve order and stable React keys, so we flatten here.
  //
  // Defensive `?? {}`: `consumeAgentDraft` already drops triage goals
  // whose `label_vocabulary` isn't a plain object, so we should never
  // see a malformed value here. The fallback exists in case a future
  // producer bypasses the validator and writes a draft directly — the
  // editor's `useState` initializer is a bad place to throw because
  // there's no error boundary above it.
  return {
    goal_type: "dataset_triage",
    label_vocabulary: Object.entries(goal.label_vocabulary ?? {}).map(
      ([label, description]) => ({ label, description }),
    ),
  };
}

/**
 * Coerces a wire-format vocabulary value into ``[key, description]`` pairs,
 * dropping anything that isn't a string-valued entry. Shared by the
 * create-events goal's two vocabularies in {@link parseGoals}.
 */
function vocabularyEntries(raw: unknown): Array<[string, string]> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return [];
  }
  const pairs: Array<[string, string]> = [];
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "string") {
      pairs.push([key, value]);
    }
  }
  return pairs;
}

/**
 * Splits the saved goals into those the editor can edit and those it keeps
 * as saved. A goal whose ``goal_type`` no branch below reads is a
 * passthrough goal, so the two lists never share a goal.
 */
function parseGoals(body: Record<string, unknown>): {
  goals: GoalState[];
  passthroughGoals: PassthroughGoal[];
} {
  const goals = body.goals;
  if (!Array.isArray(goals)) {
    return { goals: [], passthroughGoals: [] };
  }
  const parsed: GoalState[] = [];
  const passthroughGoals: PassthroughGoal[] = [];
  for (const [savedIndex, raw] of goals.entries()) {
    if (typeof raw !== "object" || raw === null) {
      continue;
    }
    const obj = raw as Record<string, unknown>;
    if (obj.goal_type === "dataset_summary") {
      parsed.push({
        goal_type: "dataset_summary",
        summary_format_spec_prompt:
          typeof obj.summary_format_spec_prompt === "string"
            ? obj.summary_format_spec_prompt
            : "",
      });
    } else if (obj.goal_type === "dataset_triage") {
      const vocab = obj.label_vocabulary;
      const rows: Array<{ label: string; description: string }> = [];
      if (
        typeof vocab === "object" &&
        vocab !== null &&
        !Array.isArray(vocab)
      ) {
        for (const [label, description] of Object.entries(
          vocab as Record<string, unknown>,
        )) {
          if (typeof description === "string") {
            rows.push({ label, description });
          }
        }
      }
      parsed.push({ goal_type: "dataset_triage", label_vocabulary: rows });
    } else if (obj.goal_type === "create_events") {
      // Both vocabularies are Records on the wire; the editor stores them as
      // ordered arrays for stable React keys and reordering. Flatten here.
      const eventVocab = vocabularyEntries(obj.event_vocabulary);
      const tagVocab = vocabularyEntries(obj.tag_vocabulary);
      parsed.push({
        goal_type: "create_events",
        event_vocabulary: eventVocab.map(([name, description]) => ({
          name,
          description,
        })),
        tag_vocabulary: tagVocab.map(([tag, description]) => ({
          tag,
          description,
        })),
        collection_enabled:
          typeof obj.collection_id === "string" && obj.collection_id !== "",
        event_focus_prompt:
          typeof obj.event_focus_prompt === "string"
            ? obj.event_focus_prompt
            : "",
      });
    } else {
      passthroughGoals.push({ savedIndex, goal: obj });
    }
  }
  return { goals: parsed, passthroughGoals };
}
